<template>
  <div>
    <Frame class="background" @click.native="close" :pose="pose" />
    <div class="embedded-image-placeholder" :style="{ '--image-width': placeholderImage.width, '--image-height': placeholderImage.height, '--offset-left' : placeholderImage.offsetLeft, '--offset-top' : placeholderImage.offsetTop }">
      <ZoomImage ref="zoom" :class="{'cursor-zoom-out' : isZoomed, 'cursor-zoom-in' : !isZoomed}" @click.native="!isMobile ? toggleZoom() : close($event)" :style="{ '--zoom-width': zoomValue, 'transform': scaleValue}" :pose="pose" :src="src" :alt="alt" />
    </div>
  </div>
</template>

<script>
import posed from 'vue-pose'
import breakpoints from '@/libs/breakpoints'

const transition = {
  duration: 500,
  ease: [0.08, 0.69, 0.2, 0.99]
}

export default {
  name: 'zoomImage',
  data: function () {
    return {
      active: false,
      isZoomed: false,
      imageData: {}
    }
  },
  computed: {
    srcLowRes: function () {
      return (this.imageData?.target?.src) ?? ''
    },
    srcHiRes: function () {
      const qualityRegex = /(.*)q75\//g
      const dimensionsRegex = /w_(.*)\//g
      return this.srcLowRes.replace(qualityRegex, '').replace(dimensionsRegex, '')
    },
    src: function () {
      return (this.isZoomed && this.$isStories) || this.isMobile ? this.srcHiRes : this.srcLowRes
    },
    alt: function () {
      return (this.imageData?.target?.alt) ?? ''
    },
    placeholderImage: function () {
      if (this.active) {
        return {
          width: `${this.imageData.target.width}px`,
          height: `${this.imageData.target.height}px`,
          offsetLeft: `${this.imageData.target.offsetLeft}px`,
          offsetTop: `${this.imageData.target.offsetTop}px`
        }
      } else {
        return {}
      }
    },
    pose: function () {
      return this.active ? 'zoomedIn' : 'zoomedOut'
    },
    zoomValue: function () {
      return this.isZoomed && this.isLargeImage ? '100vw' : 'auto'
    },
    scaleValue: function () {
      return this.isZoomed && !this.isLargeImage ? 'scale(1.5)' : 'none'
    },
    isMobile: function () {
      return breakpoints.isMobile()
    },
    isLargeImage: function () {
      return (this.imageData?.target?.width > 200 || this.imageData?.target?.height > 200) ?? true
    }
  },
  watch: {
    active: function (active) {
      if (!active) {
        this.isZoomed = false
        this.imageData = {}
      }
    }
  },
  methods: {
    getImage: function (imageData) {
      const clickedOrKeyEnter = imageData.type === 'click' || imageData.key === 'Enter'
      const hasImageData = imageData.target.src

      if (clickedOrKeyEnter && hasImageData) {
        this.imageData = imageData
        this.show()
      }
    },
    toggleListeners: function (param) {
      if (!this.isMobile) {
        window[`${param}EventListener`]('scroll', this.close)
        window[`${param}EventListener`]('keydown', this.close)
      }
    },
    show: function () {
      this.toggleListeners('add')
      this.active = true
    },
    close: function (event) {
      const onClick = event.type === 'click'
      const onScroll = event.type === 'scroll'
      const onEscKey = event.key === 'Escape'

      if (onClick || onScroll || onEscKey) {
        this.toggleListeners('remove')
        this.active = false
      }
    },
    toggleZoom: function () {
      this.isZoomed = !this.isZoomed
    }
  },
  components: {
    Frame: posed.div({
      zoomedOut: {
        applyAtEnd: { display: 'none' },
        opacity: 0
      },
      zoomedIn: {
        applyAtStart: { display: 'block' },
        opacity: 1
      }
    }),
    ZoomImage: posed.img({
      zoomedOut: {
        position: 'static',
        width: 'var(--zoom-width)',
        height: 'auto',
        transition,
        flip: true
      },
      zoomedIn: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        transition,
        flip: true
      }
    })
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';
  :root {
    --image-width: auto;
    --image-height: auto;
    --offset-left: 0;
    --offset-top: 0;
  }
  .embedded-image-placeholder {
    width: var(--image-width);
    height: var(--image-height);
    left: var(--offset-left);
    top: var(--offset-top);
    position: absolute;
    z-index: 31;
  }
  img {
    display: block;
    max-width: 100%;
    max-height: 97.5%;
    object-fit: contain;
    margin: auto;
    &:focus {
      color: $primary;
    }
    @include fullhd {
      max-width: 1100px;
    }
  }
  img.cursor-zoom-out {
    cursor: zoom-out;
  }
  img.cursor-zoom-in {
    cursor: zoom-in;
    @include tablet {
      max-width: var(--image-width);
    }
    @include mobile {
      cursor: zoom-out;
    }
  }

  .background {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: none;
    background: white;
    transform: translateZ(0);
    z-index: 30;
  }

</style>
